/**
 * /auth/forgot — Reset your password.
 *
 * Single email field → Supabase emails a reset link via
 * `resetPasswordForEmail`. On success we hand off to /auth/check-email
 * (reused as the generic "we sent you a link" surface) with the email
 * forwarded as a search param so the addressee renders.
 *
 * The reset link itself lands the user on the /auth/reset-password page where
 * they choose a new password (shared with the team-invite acceptance flow).
 *
 * 2026-06-23 (auth polish): submit moved to the shared AuthSubmit (spinner
 * busy-state); the email input keeps the global :focus-visible ring.
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AuthLayout } from '../../components/layouts/auth-layout';
import { AuthSubmit } from '../../components/auth/auth-controls';
import { BackToSignIn } from '../../components/auth/back-to-sign-in';
import { supabase } from '../../lib/supabase';
import { mapSupabaseAuthError } from '../../lib/auth-errors';
import { stashPendingAuthEmail } from '../../lib/pending-auth-email';

export const Route = createFileRoute('/auth/forgot')({
  component: ForgotPage,
});

function ForgotPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });
    setBusy(false);
    if (resetErr) {
      // Beno feedback (2026-07-15): this screen was showing the shared
      // generic fallback ("Sign-in failed…" — the wrong verb for a RESET
      // request) for every unmapped error, and the rate-limit case Supabase
      // actually returns most often here read like a generic 60-second
      // throttle rather than what it usually is: GoTrue's per-hour email
      // send cap. GoTrue signals that either with HTTP 429, the error code
      // `over_email_send_rate_limit`, or (depending on client version) just
      // matching text in the message — check all three defensively rather
      // than trust any single shape.
      const err = resetErr as { message?: string | null; status?: number; code?: string };
      const msg = (err.message ?? '').toLowerCase();
      const rateLimited =
        err.status === 429 ||
        err.code === 'over_email_send_rate_limit' ||
        msg.includes('over_email_send_rate_limit') ||
        msg.includes('rate limit') ||
        msg.includes('for security purposes');
      if (rateLimited) {
        setError('Too many emails have been sent in the last hour. Please wait a while and try again.');
        return;
      }
      const mapped = mapSupabaseAuthError(err.message);
      // Only the shared mapper's GENERIC (unmapped) fallback reads wrong on
      // this screen ("Sign-in failed…"); any other specifically-mapped
      // message is still accurate here and passes through unchanged.
      setError(
        mapped === mapSupabaseAuthError(undefined)
          ? "We couldn't send the reset email. Please try again in a moment, or contact support@elreign.io."
          : mapped,
      );
      return;
    }
    // sessionStorage, not the URL — see lib/pending-auth-email.ts.
    stashPendingAuthEmail(email);
    void navigate({ to: '/auth/check-email', search: { flow: 'forgot' } as never });
  }

  return (
    <AuthLayout
      eyebrow="Account access"
      heading="Reset your password."
      sub="Enter your email address and we will send you a reset link."
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <label
          htmlFor="forgot-email"
          style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>
            Email address
          </span>
          <input
            id="forgot-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.co.za"
            autoComplete="email"
            required
            aria-invalid={error !== null}
            aria-describedby={error !== null ? 'forgot-error' : undefined}
            disabled={busy}
            style={{
              width: '100%',
              padding: '11px 14px',
              borderRadius: 9,
              border: '1px solid var(--hairline-2)',
              background: 'var(--bg)',
              color: 'var(--ink)',
              fontFamily: 'var(--sans)',
              fontSize: 15,
              boxSizing: 'border-box',
            }}
          />
        </label>

        {error !== null && (
          <div
            id="forgot-error"
            role="alert"
            aria-live="polite"
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              background: 'rgba(220, 38, 38, 0.08)',
              border: '1px solid rgba(220, 38, 38, 0.3)',
              color: '#991B1B',
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            {error}
          </div>
        )}

        <AuthSubmit label="Send reset link" busyLabel="Sending…" busy={busy} />
      </form>

      <BackToSignIn prompt="Remembered your password?" />
    </AuthLayout>
  );
}
